import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import {
  CRM_ACTIVITY_DIRECTIONS, CRM_ACTIVITY_KINDS, CRM_CONTACT_KINDS, CRM_ERASURE_CHANNELS, CRM_LAWFUL_BASES, CRM_LIFECYCLES, CRM_PERSONAS,
  DEAL_STAGES, OPEN_DEAL_STAGES,
  type CrmAccountDetail, type CrmActivity, type CrmActivityKind, type CrmDeal, type CrmDealDetail, type CrmPerson, type DealStage,
} from '@aviaconsole/shared';
import { capi, companyUrl } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n';
import { formatDate } from '../../lib/format';
import { useToast } from '../../components/Toast';
import { BackLink, Button, Card, Chip, ConfirmDialog, Field, Input, Loading, Modal, PageHeader, Select, Textarea } from '../../components/ui';
import { crmLabel, crmOptions, lifecycleTone, localToIso, segmentLabel, stageTone, useOwners, useSegments } from '../../lib/crm';
import { AccountForm } from './CrmAccounts';
import { useMoveDeal } from './StageMove';
import { PersonEmails } from './PersonEmails';

const KIND_ICON: Record<CrmActivityKind, string> = {
  note: 'fas fa-note-sticky', call: 'fas fa-phone', email: 'fas fa-envelope', linkedin: 'fab fa-linkedin-in',
  meeting: 'fas fa-handshake', demo: 'fas fa-display', task: 'fas fa-list-check',
};

export function CrmAccountDetail() {
  const { id = '' } = useParams();
  const { role } = useAuth();
  const { t, lang } = useI18n();
  const segments = useSegments();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const admin = role === 'admin';
  // A crm member has no billing access: no customer links, no turning a deal into a customer.
  const billing = role !== 'crm';
  const q = useQuery({ queryKey: ['crm-account', id], queryFn: () => capi<CrmAccountDetail>(`/crm/accounts/${id}`) });
  const [editing, setEditing] = useState(false);
  const [addingPerson, setAddingPerson] = useState(false);
  const [addingDeal, setAddingDeal] = useState(false);
  const [optingOut, setOptingOut] = useState<CrmPerson | null>(null);
  const [erasing, setErasing] = useState<CrmPerson | null>(null);
  const [addresses, setAddresses] = useState<CrmPerson | null>(null);
  const { move, dialog } = useMoveDeal();
  const refresh = () => { qc.invalidateQueries({ queryKey: ['crm-account', id] }); qc.invalidateQueries({ queryKey: ['crm-accounts'] }); };
  const fail = (e: Error) => toast.push(e.message, 'error');

  const lifecycle = useMutation({ mutationFn: (l: string) => capi(`/crm/accounts/${id}`, { method: 'PATCH', json: { lifecycle: l } }), onSuccess: refresh, onError: fail });
  const optOut = useMutation({
    mutationFn: (p: CrmPerson) => capi(`/crm/people/${p.id}/opt-out`, { method: 'POST', json: { channel: 'recorded by staff' } }),
    onSuccess: () => { refresh(); setOptingOut(null); toast.push(t('done')); }, onError: fail,
  });
  const convert = useMutation({
    mutationFn: (dealId: string) => capi<CrmDealDetail>(`/crm/deals/${dealId}/convert`, { method: 'POST', json: {} }),
    onSuccess: () => { refresh(); toast.push(t('done')); }, onError: fail,
  });

  if (!q.data) return <Loading error={q.error} />;
  const a = q.data;
  const lifecycleChoices = CRM_LIFECYCLES.filter((l) => l !== 'customer' || a.lifecycle === 'customer');

  return (
    <>
      <BackLink to="/crm/accounts" label={t('accTitle')} />
      <PageHeader
        crumb={`${t('crmCrumb')} · ${a.country}${a.priority ? ` · P${a.priority}` : ''}`} title={a.name}
        sub={[segmentLabel(segments.data, a.segment, lang), a.operatorType && crmLabel('operatorType', a.operatorType, lang), a.ownerName].filter(Boolean).join(' · ')}
        actions={<>
          <Chip tone={lifecycleTone(a.lifecycle)}>{crmLabel('lifecycle', a.lifecycle, lang)}</Chip>
          <select className="inline" aria-label={t('fLifecycle')} value={a.lifecycle} disabled={a.lifecycle === 'customer'} onChange={(e) => lifecycle.mutate(e.target.value)}>
            {lifecycleChoices.map((l) => <option key={l} value={l} disabled={l === 'customer'}>{crmLabel('lifecycle', l, lang)}</option>)}
          </select>
          {billing && a.customerId && <Button icon="fas fa-address-book" onClick={() => navigate(`/customers/${a.customerId}`)}>{t('openCustomer')}</Button>}
          <Button icon="fas fa-pen" onClick={() => setEditing(true)}>{t('hDetails')}</Button>
        </>}
      />
      <div className="grid two">
        <div className="stack">
          <Card pad={false} className="table">
            <div className="cardhead"><h2>{t('hPeople')}</h2><Button size="sm" icon="fas fa-user-plus" onClick={() => setAddingPerson(true)}>{t('addPerson')}</Button></div>
            <table><tbody>
              {a.people.length === 0 && <tr className="empty"><td colSpan={9}>{t('crmNone')}</td></tr>}
              {a.people.map((p) => (
                <tr key={p.id}>
                  <td><div style={{ fontWeight: 600 }}>{p.name}</div><div className="sub">{[p.roleTitle, crmLabel('persona', p.persona, lang)].filter(Boolean).join(' · ')}</div></td>
                  <td className="small"><div>{p.email ?? '—'}{p.emails.length > 1 && <span className="muted"> +{p.emails.length - 1}</span>}</div><div className="sub">{p.phone ?? ''}{p.linkedinUrl ? <> · <a href={p.linkedinUrl.startsWith('http') ? p.linkedinUrl : `https://${p.linkedinUrl}`} target="_blank" rel="noreferrer">LinkedIn</a></> : null}</div></td>
                  <td className="small"><div>{crmLabel('basis', p.lawfulBasis, lang)}</div><div className="sub" title={p.source}>{p.source}</div></td>
                  <td>{p.optedOutAt ? <Chip tone="danger" icon="fas fa-ban">{t('optedOutOn', { date: formatDate(p.optedOutAt, lang) })}</Chip> : p.lastContactedAt ? <span className="small muted">{formatDate(p.lastContactedAt, lang)}</span> : null}</td>
                  <td className="r"><span className="row" style={{ justifyContent: 'flex-end' }}>
                    <button type="button" className="linkbtn" onClick={() => setAddresses(p)}>{t('emAddresses')}</button>
                    {!p.optedOutAt && <button type="button" className="linkbtn" onClick={() => setOptingOut(p)}>{t('optOut')}</button>}
                    {admin && <a className="linkbtn" href={companyUrl(`/crm/people/${p.id}/export`)}>{t('exportData')}</a>}
                    {admin && <button type="button" className="linkbtn" style={{ color: 'var(--danger)' }} onClick={() => setErasing(p)}>{t('erase')}</button>}
                  </span></td>
                </tr>
              ))}
            </tbody></table>
          </Card>

          <Card pad={false}>
            <div className="cardhead"><h2>{t('hDeals')}</h2><Button size="sm" icon="fas fa-plus" onClick={() => setAddingDeal(true)}>{t('newDeal')}</Button></div>
            <div className="stack sm" style={{ padding: '0.8rem 1rem' }}>
              {a.deals.length === 0 && <div className="small muted">{t('crmNone')}</div>}
              {a.deals.map((d) => <DealRow key={d.id} deal={d} canConvert={billing && !a.customerId} onMove={(to) => move(d, to)} onConvert={() => convert.mutate(d.id)} converting={convert.isPending} />)}
            </div>
          </Card>

          <Timeline account={a} onChange={refresh} />
        </div>

        <div className="stack">
          <Card>
            <h2>{t('hDetails')}</h2>
            <div className="grid fields">
              <Field label={t('fWebsite')} value={a.website ? <a href={a.website.startsWith('http') ? a.website : `https://${a.website}`} target="_blank" rel="noreferrer">{a.website}</a> : null} />
              <Field label={t('fFleet')} value={a.fleetSize} />
              <Field label={t('fAircraft')} value={a.mainAircraft} />
              <Field label={t('fAoc')} value={a.aocReference} />
              <Field label={t('fSource')} value={crmLabel('source', a.source, lang)} hint={a.sourceRef ?? undefined} />
              {billing && <Field label={t('linkedCustomer')} value={a.customerId ? <Link to={`/customers/${a.customerId}`}>{t('openCustomer')}</Link> : null} />}
            </div>
            <div className="stack md" style={{ marginTop: '0.75rem' }}>
              {a.triggerNote && <Field label={t('fTrigger')} value={a.triggerNote} />}
              {a.smsApproach && <Field label={t('fSms')} value={<span style={{ whiteSpace: 'pre-wrap' }}>{a.smsApproach}</span>} />}
              {a.sourceUrl && <Field label={t('fSourceUrl')} value={<a href={a.sourceUrl} target="_blank" rel="noreferrer">{a.sourceUrl}</a>} />}
              {a.notes && <Field label={t('fNotes')} value={<span style={{ whiteSpace: 'pre-wrap' }}>{a.notes}</span>} />}
            </div>
          </Card>
        </div>
      </div>

      {editing && <AccountForm account={a} onClose={() => setEditing(false)} onSaved={() => refresh()} />}
      {addingPerson && <PersonForm accountId={a.id} onClose={() => setAddingPerson(false)} onSaved={refresh} />}
      {addingDeal && <DealForm account={a} onClose={() => setAddingDeal(false)} onSaved={refresh} />}
      {optingOut && (
        <ConfirmDialog title={t('confirmOptOutTitle', { name: optingOut.name })} confirmLabel={t('optOut')} cancelLabel={t('cancel')} danger pending={optOut.isPending}
          onClose={() => setOptingOut(null)} onConfirm={() => optOut.mutate(optingOut)}>{t('confirmOptOutBody')}</ConfirmDialog>
      )}
      {erasing && <EraseDialog person={erasing} onClose={() => setErasing(null)} onDone={refresh} />}
      {addresses && <PersonEmails personId={addresses.id} name={addresses.name} onClose={() => setAddresses(null)} onChange={refresh} />}
      {dialog}
    </>
  );
}

function DealRow({ deal: d, canConvert, onMove, onConvert, converting }: {
  deal: CrmDeal; canConvert: boolean; onMove: (to: DealStage) => void; onConvert: () => void; converting: boolean;
}) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const detail = useQuery({ queryKey: ['crm-deal', d.id], enabled: open, queryFn: () => capi<CrmDealDetail>(`/crm/deals/${d.id}`) });
  const targets = DEAL_STAGES.filter((s) => s !== 'paying' && (s === 'lost' || DEAL_STAGES.indexOf(s) > DEAL_STAGES.indexOf(d.stage)));
  const closed = d.stage === 'paying' || d.stage === 'lost';
  return (
    <div className="subcard stack sm">
      <div className="row">
        <span style={{ fontWeight: 600, fontSize: '0.86rem' }}>{d.title}</span>
        <Chip tone={stageTone(d.stage)}>{crmLabel('stage', d.stage, lang)}</Chip>
        <span className="small muted">{[d.primaryPersonName, d.ownerName, d.wonAt && `${t('fContractDate')} ${formatDate(d.wonAt, lang)}`, d.paidAt && `${crmLabel('stage', 'paying', lang)} ${formatDate(d.paidAt, lang)}`].filter(Boolean).join(' · ')}</span>
        <div className="spacer" />
        {(d.stage === 'won' || d.stage === 'paying') && canConvert && <Button size="sm" variant="primary" icon="fas fa-user-check" disabled={converting} onClick={onConvert}>{t('convert')}</Button>}
        {!closed && (
          <select className="inline" aria-label={`${t('moveTo')} ${d.title}`} value="" onChange={(e) => { if (e.target.value) onMove(e.target.value as DealStage); }}>
            <option value="">{t('moveTo')}</option>
            {targets.map((s) => <option key={s} value={s}>{crmLabel('stage', s, lang)}</option>)}
          </select>
        )}
        <button type="button" className="linkbtn" aria-expanded={open} onClick={() => setOpen(!open)}>{t('stageHistory')}</button>
      </div>
      {d.proposalUrl && <a className="small" href={d.proposalUrl} target="_blank" rel="noreferrer">{t('fProposalUrl')}</a>}
      {d.lostReason && <span className="small muted">{crmLabel('lostReason', d.lostReason, lang)}{d.lostNote ? `: ${d.lostNote}` : ''}</span>}
      {open && detail.data && (
        <ol className="small secondary" style={{ margin: 0, paddingLeft: '1.2rem' }}>
          {detail.data.stageEvents.map((e) => <li key={e.id}>{formatDate(e.at, lang, true)} · {e.fromStage ? `${crmLabel('stage', e.fromStage, lang)} → ` : ''}{crmLabel('stage', e.toStage, lang)}{e.byName ? ` · ${e.byName}` : ''}</li>)}
        </ol>
      )}
    </div>
  );
}

function Timeline({ account, onChange }: { account: CrmAccountDetail; onChange: () => void }) {
  const { t, lang } = useI18n();
  const toast = useToast();
  const empty = { kind: 'note' as CrmActivityKind, direction: '', personId: '', dealId: '', subject: '', body: '', occurredAt: '', dueAt: '' };
  const [f, setF] = useState(empty);
  const isContact = CRM_CONTACT_KINDS.includes(f.kind);
  const log = useMutation({
    mutationFn: () => capi<CrmActivity>('/crm/activities', {
      method: 'POST', json: {
        accountId: account.id, kind: f.kind, subject: f.subject, body: f.body || undefined,
        direction: isContact ? f.direction || undefined : undefined, personId: f.personId || undefined, dealId: f.dealId || undefined,
        occurredAt: f.kind === 'task' ? undefined : localToIso(f.occurredAt), dueAt: f.kind === 'task' ? localToIso(f.dueAt) : undefined,
      },
    }),
    onSuccess: () => { setF(empty); onChange(); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const complete = useMutation({
    mutationFn: (a: CrmActivity) => capi(`/crm/activities/${a.id}`, { method: 'PATCH', json: { done: !a.doneAt } }),
    onSuccess: onChange, onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const people = account.people.filter((p) => !(isContact && f.direction === 'outbound' && p.optedOutAt));
  return (
    <Card>
      <h2>{t('hTimeline')}</h2>
      <form className="stack md" onSubmit={(e) => { e.preventDefault(); log.mutate(); }}>
        <div className="grid fields">
          <Select label={t('fKind')} options={crmOptions('kind', CRM_ACTIVITY_KINDS, lang)} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as CrmActivityKind })} />
          {isContact && <Select label={t('fDirection')} required options={crmOptions('direction', CRM_ACTIVITY_DIRECTIONS, lang, '—')} value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} />}
          <Select label={t('hPeople')} options={[{ value: '', label: '—' }, ...people.map((p) => ({ value: p.id, label: p.name }))]} value={f.personId} onChange={(e) => setF({ ...f, personId: e.target.value })} />
          <Select label={t('hDeals')} options={[{ value: '', label: '—' }, ...account.deals.map((d) => ({ value: d.id, label: d.title }))]} value={f.dealId} onChange={(e) => setF({ ...f, dealId: e.target.value })} />
          {f.kind === 'task'
            ? <Input type="datetime-local" required label={t('fDueAt')} value={f.dueAt} onChange={(e) => setF({ ...f, dueAt: e.target.value })} />
            : <Input type="datetime-local" label={t('fOccurredAt')} value={f.occurredAt} onChange={(e) => setF({ ...f, occurredAt: e.target.value })} />}
        </div>
        <Input label={t('fSubject')} required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
        <div className="small" style={{ color: 'var(--warning)' }}><i className="fas fa-eye" aria-hidden="true" /> {t('notesWarning')}</div>
        <Textarea label={t('fBody')} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
        <div className="row" style={{ justifyContent: 'flex-end' }}><Button type="submit" variant="primary" icon="fas fa-plus" disabled={log.isPending}>{f.kind === 'task' ? t('addTask') : t('logActivity')}</Button></div>
      </form>
      <div className="timeline" style={{ marginTop: '1rem' }}>
        {account.activities.map((a) => (
          <div key={a.id} className="entry">
            <span className="chip dot neutral"><i className={KIND_ICON[a.kind]} aria-hidden="true" /></span>
            <div>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600, fontSize: '0.84rem', textDecoration: a.doneAt ? 'line-through' : undefined }}>{a.subject}</span>
                <span className="mono small muted">{formatDate(a.kind === 'task' ? a.dueAt : a.occurredAt, lang, true)}</span>
              </div>
              <div className="small muted">
                {[crmLabel('kind', a.kind, lang), a.direction && crmLabel('direction', a.direction, lang), a.personName, a.createdByName ?? a.createdVia].filter(Boolean).join(' · ')}
                {a.kind === 'task' && <> · <button type="button" className="linkbtn" onClick={() => complete.mutate(a)}>{a.doneAt ? '↺' : t('markDone')}</button></>}
              </div>
              {a.body && <p>{a.body}</p>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PersonForm({ accountId, onClose, onSaved }: { accountId: string; onClose: () => void; onSaved: () => void }) {
  const { t, lang } = useI18n();
  const toast = useToast();
  const [f, setF] = useState({ name: '', firstName: '', lastName: '', initials: '', roleTitle: '', persona: 'other', email: '', phone: '', linkedinUrl: '', lawfulBasis: 'legitimate_interest', basisNote: '', source: '' });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => capi<CrmPerson>('/crm/people', { method: 'POST', json: { accountId, ...Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim() === '' ? undefined : v])) } }),
    onSuccess: () => { onSaved(); onClose(); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  return (
    <Modal title={t('addPerson')} onClose={onClose}>
      <form className="stack md" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid fields">
          <Input label={t('fName')} required value={f.name} onChange={set('name')} />
          <Input label={t('fRoleTitle')} value={f.roleTitle} onChange={set('roleTitle')} />
          <Input label={t('fFirstName')} value={f.firstName} onChange={set('firstName')} />
          <Input label={t('fLastName')} value={f.lastName} onChange={set('lastName')} />
          <Input label={t('fInitials')} value={f.initials} onChange={set('initials')} placeholder="J.P." />
          <Select label={t('fPersona')} options={crmOptions('persona', CRM_PERSONAS, lang)} value={f.persona} onChange={set('persona')} />
          <Input label={t('fEmail')} type="email" value={f.email} onChange={set('email')} />
          <Input label={t('fPhone')} value={f.phone} onChange={set('phone')} />
          <Input label={t('fLinkedin')} value={f.linkedinUrl} onChange={set('linkedinUrl')} />
          <Select label={t('fBasis')} options={crmOptions('basis', CRM_LAWFUL_BASES, lang)} value={f.lawfulBasis} onChange={set('lawfulBasis')} />
          <Input label={t('fBasisNote')} value={f.basisNote} onChange={set('basisNote')} />
        </div>
        <Input label={t('fSourcePerson')} required value={f.source} onChange={set('source')} />
        <div className="row" style={{ justifyContent: 'flex-end' }}><Button onClick={onClose}>{t('cancel')}</Button><Button variant="primary" type="submit" disabled={save.isPending}>{t('crmCreate')}</Button></div>
      </form>
    </Modal>
  );
}

function DealForm({ account, onClose, onSaved }: { account: CrmAccountDetail; onClose: () => void; onSaved: () => void }) {
  const { t, lang } = useI18n();
  const toast = useToast();
  const owners = useOwners();
  const [f, setF] = useState({ title: '', stage: 'qualified', ownerUserId: account.ownerUserId ?? '', primaryPersonId: '', modules: '', expectedCloseDate: '' });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => capi<CrmDealDetail>('/crm/deals', {
      method: 'POST', json: {
        accountId: account.id, title: f.title, stage: f.stage, ownerUserId: f.ownerUserId || undefined, primaryPersonId: f.primaryPersonId || undefined,
        modules: f.modules.split(',').map((m) => m.trim()).filter(Boolean), expectedCloseDate: f.expectedCloseDate || undefined,
      },
    }),
    onSuccess: () => { onSaved(); onClose(); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  return (
    <Modal title={t('newDeal')} onClose={onClose}>
      <form className="stack md" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Input label={t('fTitle')} required value={f.title} onChange={set('title')} />
        <div className="grid fields">
          <Select label={t('fStage')} options={crmOptions('stage', OPEN_DEAL_STAGES, lang)} value={f.stage} onChange={set('stage')} />
          <Select label={t('fOwner')} options={[{ value: '', label: t('noOwner') }, ...(owners.data ?? []).map((o) => ({ value: o.userId, label: o.name }))]} value={f.ownerUserId} onChange={set('ownerUserId')} />
          <Select label={t('fPrimaryPerson')} options={[{ value: '', label: '—' }, ...account.people.map((p) => ({ value: p.id, label: p.name }))]} value={f.primaryPersonId} onChange={set('primaryPersonId')} />
          <Input type="date" label={t('fExpectedClose')} value={f.expectedCloseDate} onChange={set('expectedCloseDate')} />
        </div>
        <Input label={t('fModules')} value={f.modules} onChange={set('modules')} />
        <div className="row" style={{ justifyContent: 'flex-end' }}><Button onClick={onClose}>{t('cancel')}</Button><Button variant="primary" type="submit" disabled={save.isPending}>{t('crmCreate')}</Button></div>
      </form>
    </Modal>
  );
}

function EraseDialog({ person, onClose, onDone }: { person: CrmPerson; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [f, setF] = useState({ requestedVia: 'email', reason: 'Erasure request' });
  const erase = useMutation({
    mutationFn: () => capi<{ note: string }>(`/crm/people/${person.id}/erase`, { method: 'POST', json: f }),
    onSuccess: (r) => { onDone(); onClose(); toast.push(r.note); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  return (
    <Modal title={t('eraseTitle', { name: person.name })} onClose={onClose}>
      <form className="stack md" onSubmit={(e) => { e.preventDefault(); erase.mutate(); }}>
        <p className="secondary" style={{ margin: 0, fontSize: '0.86rem' }}>{t('eraseBody')}</p>
        <Select label={t('eraseVia')} options={CRM_ERASURE_CHANNELS.map((c) => ({ value: c, label: c }))} value={f.requestedVia} onChange={(e) => setF({ ...f, requestedVia: e.target.value })} />
        <Input label={t('eraseReason')} required value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button autoFocus onClick={onClose}>{t('cancel')}</Button>
          <Button variant="danger" type="submit" icon="fas fa-eraser" disabled={erase.isPending}>{t('erase')}</Button>
        </div>
      </form>
    </Modal>
  );
}
